import { TableToolbarButtonProps } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { TooltipButton } from '@dealstack/frontend/shared/components/Table/MantineTable/TooltipButton/TooltipButton';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Table } from '@tanstack/react-table';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

enum PARAM_KEYS {
	SHOW_FILTERS = 'showFilters',
}

export const ToggleFiltersButton = <TData extends RecordAny>({
	table,
	...rest
}: TableToolbarButtonProps<TData>) => {
	const tCommonUi = useTranslationCommonUi();
	const [searchParams, setSearchParams] = useSearchParams();

	type Meta = { showColumnFilters?: boolean };
	type Icons = { IconFilter?: React.FC; IconFilterOff?: React.FC };
	type Localization = { showHideFilters?: string };
	const tt = table as Table<TData> & {
		options: {
			icons?: Icons;
			localization?: Localization;
			meta?: Meta & { setShowColumnFilters?: (v: boolean) => void };
		};
	};
	const icons = tt.options.icons ?? {};
	const localization = tt.options.localization ?? {};
	const IconFilter = icons.IconFilter ?? (() => null);
	const IconFilterOff = icons.IconFilterOff ?? (() => null);
	const showColumnFilters = getTableMeta(table).showColumnFilters;
	const setShowColumnFilters = getTableMeta(table).setShowColumnFilters;

	useEffect(() => {
		const urlShowFilters = searchParams.get(PARAM_KEYS.SHOW_FILTERS);

		if (urlShowFilters == null) {
			return;
		}

		setShowColumnFilters?.(urlShowFilters === 'true');
	}, [searchParams, setShowColumnFilters]);

	const handleToggleShowFilters = () => {
		const newValue = !showColumnFilters;

		setShowColumnFilters?.(newValue);

		const newParams = new URLSearchParams(searchParams);
		if (newValue) {
			newParams.set(PARAM_KEYS.SHOW_FILTERS, 'true');
		} else {
			newParams.delete(PARAM_KEYS.SHOW_FILTERS);
		}
		setSearchParams(newParams, { replace: true });
	};

	return (
		<TooltipButton
			tooltipLabel={rest?.title ?? localization.showHideFilters}
			onClick={handleToggleShowFilters}
			active={showColumnFilters}
			text={tCommonUi('mantine_table.top_toolbar.filters')}
			leftSection={showColumnFilters ? <IconFilterOff /> : <IconFilter />}
			style={{
				width: '7.125rem',
			}}
			{...rest}
		/>
	);
};
